<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import {
  generateReportURLParams,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    range: selectedDateRange.value,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
  });
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }
};

onMounted(() => {
  initializeFromURL();
  emitChange();
});
</script>

<template>
  <div
    data-test-id="overviewReportFilters"
    class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-solid-1 px-4 py-3 shadow-sm dark:border-n-strong md:flex-row md:items-center md:justify-between"
    :class="{ 'pointer-events-none opacity-60': disabled }"
    :aria-busy="disabled"
  >
    <div class="flex min-w-0 flex-wrap items-center gap-2">
      <span
        class="i-hugeicons-filter size-4 shrink-0 text-n-slate-10"
        aria-hidden="true"
      />
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
    </div>
    <div
      class="flex items-center gap-2.5 rounded-lg bg-n-alpha-1 px-3 py-2 md:bg-transparent md:px-0 md:py-0"
    >
      <span
        class="i-hugeicons-time-schedule size-4 shrink-0 text-n-slate-10"
        aria-hidden="true"
      />
      <span
        id="report-business-hours-label"
        class="whitespace-nowrap text-sm text-n-slate-12"
      >
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </span>
      <ToggleSwitch
        v-model="businessHoursSelected"
        v-tooltip.top="$t('SUMMARY_REPORTS.BUSINESS_HOURS_HINT')"
        aria-labelledby="report-business-hours-label"
        @change="onBusinessHoursToggle"
      />
    </div>
  </div>
</template>
